import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const parseEmails = (raw) => {
  return raw
    .split(/[\s,;]+/)
    .map((e) => e.trim())
    .filter(Boolean);
};

export default function ShareLectureDialog({ open, onOpenChange, lecture }) {
  const [emailsRaw, setEmailsRaw] = useState('');
  const [role, setRole] = useState('viewer');
  const [loading, setLoading] = useState(false);
  const [collaborators, setCollaborators] = useState([]);

  const lectureId = lecture?.id;

  const emails = useMemo(() => parseEmails(emailsRaw), [emailsRaw]);

  useEffect(() => {
    if (!open || !lectureId) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/collaboration/collaborators/${lectureId}`);
        setCollaborators(res.data?.collaborators || []);
      } catch (e) {
        setCollaborators([]);
      }
    })();
  }, [open, lectureId]);

  const refresh = async () => {
    if (!lectureId) return;
    const res = await axios.get(`${API}/collaboration/collaborators/${lectureId}`);
    setCollaborators(res.data?.collaborators || []);
  };

  const share = async () => {
    if (!lectureId) return;
    if (emails.length === 0) {
      toast.error('Add at least one email');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/collaboration/share`, {
        lecture_id: lectureId,
        emails,
        role
      });
      toast.success('Shared!');
      setEmailsRaw('');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to share');
    } finally {
      setLoading(false);
    }
  };

  const remove = async (email) => {
    if (!lectureId) return;
    setLoading(true);
    try {
      await axios.delete(`${API}/collaboration/collaborator`, {
        data: { lecture_id: lectureId, email }
      });
      toast.success('Removed collaborator');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove');
    } finally {
      setLoading(false);
    }
  };

  const changeRole = async (email, newRole) => {
    if (!lectureId) return;
    setLoading(true);
    try {
      await axios.put(`${API}/collaboration/collaborator`, {
        lecture_id: lectureId,
        email,
        role: newRole
      });
      toast.success('Role updated');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update role');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-sm" data-testid="share-lecture-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Share lecture</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Invite by email</Label>
            <Input
              placeholder="comma-separated emails (e.g. a@x.com, b@y.com)"
              value={emailsRaw}
              onChange={(e) => setEmailsRaw(e.target.value)}
              className="rounded-sm"
              data-testid="share-emails-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Permission</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="rounded-sm" data-testid="share-role-select">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="viewer">Viewer</SelectItem>
                <SelectItem value="editor">Editor</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            onClick={share}
            disabled={loading || !lectureId}
            className="rounded-sm w-full"
            data-testid="share-submit"
          >
            {loading ? 'Sharing...' : 'Share'}
          </Button>

          <div className="border-t border-border pt-4">
            <div className="text-sm font-medium mb-2">Collaborators</div>
            {collaborators.length === 0 ? (
              <div className="text-sm text-muted-foreground">No collaborators yet.</div>
            ) : (
              <div className="space-y-2">
                {collaborators.map((c) => (
                  <div
                    key={c.email}
                    className="flex items-center justify-between gap-2 p-2 rounded-sm border border-border"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{c.email}</div>
                      <div className="text-xs text-muted-foreground">{c.role}</div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Select value={c.role} onValueChange={(v) => changeRole(c.email, v)}>
                        <SelectTrigger className="h-8 w-28 rounded-sm" data-testid={`collab-role-${c.email}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="viewer">Viewer</SelectItem>
                          <SelectItem value="editor">Editor</SelectItem>
                        </SelectContent>
                      </Select>

                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-sm"
                        onClick={() => remove(c.email)}
                        disabled={loading}
                        data-testid={`remove-collab-${c.email}`}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
